import { Node } from "react-checkbox-tree";

import { hydrateLayer } from "fond/api";
import { InferredIcon } from "fond/map/Legend/InferredIcon";
import { Configuration, GroupConfig } from "fond/types";
import { FilterConfiguration, LayerConfig, LayerStyle, SublayerConfig, SublayerConfigHydrated } from "fond/types/ProjectLayerConfig";

/**
 * Extracts all attribute names referenced in a FilterConfiguration
 */
export const extractAttributesFromFilter = (filter: FilterConfiguration | null): string[] => {
  if (!filter) return [];

  const attributes: Set<string> = new Set();

  const traverse = (node: FilterConfiguration) => {
    if (!node) return;

    // Check if this node has Values array with Attribute references
    if ("Values" in node && node.Values && Array.isArray(node.Values)) {
      node.Values.forEach((value) => {
        if (value.Attribute?.Name) {
          attributes.add(value.Attribute.Name);
        }
      });
    }

    // Recursively check nested Expressions
    if (node.Expressions && Array.isArray(node.Expressions)) {
      node.Expressions.forEach((expr) => traverse(expr));
    }
  };

  traverse(filter);
  return [...attributes];
};

/**
 * Transforms a source sublayer config into a hydrated format ready to be added to a target layer.
 * This removes the ID so a new one will be generated by the backend, and sets the ParentID to the target layer.
 */
export const transformSublayerForCopying = (
  sourceSublayer: SublayerConfig,
  sourceConfig: Configuration,
  targetLayerId: string
): Omit<SublayerConfigHydrated, "ID"> => {
  // Hydrate the sublayer to get full style objects
  const hydrated = hydrateLayer(sourceConfig.Data.entities, sourceSublayer, undefined, true) as SublayerConfigHydrated;

  // Return the sublayer without ID so backend generates a new one, but with the correct ParentID
  return {
    ...hydrated,
    ParentID: targetLayerId,
  };
};

/**
 * Generates an array of nodes representing a hierarchical tree structure of groups, layers, sublayers, and styles
 * to be displayed in a checkbox tree. Each node represents a configuration entity (group, layer, sublayer, or style)
 * with its children nested underneath.
 *
 * @param config - The version configuration containing all entities (groups, layers, sublayers, styles)
 * @param excludedKeys - Array of layer/sublayer keys to exclude from the tree
 * @param allowSublayerSelection - Whether to include sublayers in the tree (default: true)
 * @returns An array of tree nodes suitable for react-checkbox-tree, with icons for layers, sublayers, and styles.
 */
export const generateLayerStyleTreeItems = ({
  config,
  excludedKeys,
  allowSublayerSelection = true,
}: {
  config: Configuration;
  excludedKeys: string[];
  allowSublayerSelection?: boolean;
}): Array<Node> => {
  const items: Node[] = [];

  const add = (item: GroupConfig | LayerConfig | SublayerConfig | undefined): Node | null => {
    if (!item || (item.Key && excludedKeys.includes(item.Key))) return null;

    // Exclude comment layers and groups
    if (item.Type === "CommentConfig") return null;
    if ("IsCommentLayer" in item && item.IsCommentLayer) return null;
    if (item.ID === "projectComments") return null;

    // Add styles as children
    const styleNodes: Node[] = [];
    if (item.Type === "LAYER" || item.Type === "SUBLAYER") {
      item.Styles.forEach((styleId) => {
        const style = config.Data.entities[styleId] as LayerStyle;
        const styleItem: Node = {
          value: style.ID,
          label: (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
              <InferredIcon entity={style} config={config} />
              {style.Name}
            </span>
          ),
          className: "STYLE",
        };
        styleNodes.push(styleItem);
      });
    }

    const children = item.Type !== "SUBLAYER" ? item.Children : [];
    if (item && item.Key) {
      const entity = config.Data.entities[item.ID];

      const newItem: Node = {
        value: item.ID,
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            {entity && entity.Type !== "GROUP" && <InferredIcon entity={entity} config={config} />}
            {item.Label}
          </span>
        ),
        className: item.Type,
        children: styleNodes,
        // Always disable groups (organizational containers - you copy their contents, not the containers themselves)
        // Disable sublayers when copying to a sublayer (sublayers can't contain other sublayers)
        disabled: item.Type === "GROUP" || ((item.Type === "SUBLAYER" || item.Type === "LAYER") && !allowSublayerSelection),
      };

      children.forEach((id) => {
        const childEntity = config.Data.entities[id];
        const child = childEntity && childEntity?.Type !== "STYLE" ? add(childEntity) : null;
        if (child) newItem.children?.push(child);
      });

      return newItem;
    }
    return null;
  };

  config.MapChildren.forEach((childId) => {
    const item = config.Data.entities[childId];
    // Skip styles, comment layers, and projectComments group at the root level
    if (item && item.Type !== "STYLE" && item.Type !== "CommentConfig" && item.ID !== "projectComments") {
      const node = add(item);
      if (node) items.push(node);
    }
  });

  return items;
};
